import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  ApiErrorBody,
  CreateReferralCodeInput,
  PanelReferralCodeDto,
  PanelReferralCodeRedemptionDto,
  UpdateReferralCodeInput,
} from '@icebreaker/shared';
import { createAsyncActions } from '@icebreaker/shared/store';

/**
 * REFERANS (HEDİYE) KODLARI DEPOSU (P7 · referans kodu kapsamı Story 1).
 *
 * Liste bütün kodları taşır (sayfalar sagada birleştirilir): sekme sayaçları
 * ve durum etiketi istemcide hesaplanır, sunucuya ikinci bir sayaç sorusu
 * gitmez. İnvalidation ELLE ve AÇIK (`INVALIDATION_MAP` `panelReferral/*`):
 * oluşturma ve düzenleme sonrası liste sunucudan tazelenir; yanıtın kendisi
 * de beklemeden yerine yazılır (yeni satır en üstte vurgulu görünür).
 *
 * KULLANIM LİSTESİ (Story 6) kenar katmanı her açıldığında sunucudan okunur
 * (önbellek yok, invalidation gerekmez). Hangi koda ait olduğu `codeId` ile
 * tutulur: başka bir kod açılınca önceki kodun satırları çizilmez.
 */
export const fetchReferralCodes = createAsyncActions<void, PanelReferralCodeDto[]>(
  'panelReferral/listFetch',
);
export const createReferralCode = createAsyncActions<CreateReferralCodeInput, PanelReferralCodeDto>(
  'panelReferral/create',
);
export const updateReferralCode = createAsyncActions<
  { id: string; body: UpdateReferralCodeInput },
  PanelReferralCodeDto
>('panelReferral/update');

export const fetchReferralRedemptions = createAsyncActions<
  { id: string },
  { id: string; items: PanelReferralCodeRedemptionDto[] }
>('panelReferral/redemptionsFetch');

export type ReferralTab = 'all' | 'active' | 'inactive';

type Phase = 'idle' | 'loading' | 'succeeded' | 'failed';

export type ReferralWriteKey = 'create' | 'update';

interface WriteState {
  phase: Phase;
  error: ApiErrorBody | null;
}

export interface ReferralCodesState {
  list: {
    items: PanelReferralCodeDto[];
    phase: Phase;
    error: ApiErrorBody | null;
    /** Liste en az bir kez geldi mi (ilk yükleme iskeleti ile tazeleme ayrılır). */
    loaded: boolean;
  };
  redemptions: {
    /** Satırların ait olduğu kod; istek sürerken açılan kodun kimliği. */
    codeId: string | null;
    items: PanelReferralCodeRedemptionDto[];
    phase: Phase;
    error: ApiErrorBody | null;
  };
  tab: ReferralTab;
  writes: Record<ReferralWriteKey, WriteState>;
  /** Başarılı oluşturmadan sonra vurgulanan satır (P7 "BAŞARILI · SATIR VURGULU"). */
  highlightId: string | null;
  /** Oluşturma bildirimi: kodun adı. `null` = bildirim yok. */
  notice: string | null;
}

const idleWrite: WriteState = { phase: 'idle', error: null };

const initialState: ReferralCodesState = {
  list: { items: [], phase: 'idle', error: null, loaded: false },
  redemptions: { codeId: null, items: [], phase: 'idle', error: null },
  tab: 'all',
  writes: { create: idleWrite, update: idleWrite },
  highlightId: null,
  notice: null,
};

const slice = createSlice({
  name: 'panelReferral',
  initialState,
  reducers: {
    tabChanged(state, action: PayloadAction<ReferralTab>) {
      state.tab = action.payload;
    },
    /** Diyalog açılırken ve kapanırken önceki yazmanın hâli silinir. */
    writeReset(state, action: PayloadAction<ReferralWriteKey>) {
      state.writes[action.payload] = idleWrite;
    },
    noticeDismissed(state) {
      state.notice = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReferralCodes.request, (state) => {
        state.list.phase = 'loading';
        state.list.error = null;
      })
      .addCase(fetchReferralCodes.success, (state, action) => {
        state.list.items = action.payload;
        state.list.phase = 'succeeded';
        state.list.loaded = true;
      })
      .addCase(fetchReferralCodes.failure, (state, action) => {
        state.list.phase = 'failed';
        state.list.error = action.payload;
      })
      .addCase(fetchReferralRedemptions.request, (state, action) => {
        state.redemptions = {
          codeId: action.payload.id,
          items: [],
          phase: 'loading',
          error: null,
        };
      })
      .addCase(fetchReferralRedemptions.success, (state, action) => {
        // Geç gelen eski kodun cevabı açık kodun satırlarını ezmez.
        if (action.payload.id !== state.redemptions.codeId) return;
        state.redemptions.items = action.payload.items;
        state.redemptions.phase = 'succeeded';
      })
      .addCase(fetchReferralRedemptions.failure, (state, action) => {
        state.redemptions.phase = 'failed';
        state.redemptions.error = action.payload;
      })
      .addCase(createReferralCode.request, (state) => {
        state.writes.create = { phase: 'loading', error: null };
      })
      .addCase(createReferralCode.success, (state, action) => {
        state.writes.create = { phase: 'succeeded', error: null };
        const created = action.payload;
        state.list.items = [created, ...state.list.items.filter((item) => item.id !== created.id)];
        state.highlightId = created.id;
        state.notice = created.name;
        // Yeni satır yalnız Pasif sekmesinde gizli kalabilir: görünmesi için Tümü'ne dönülür.
        if (state.tab !== 'all') state.tab = 'all';
      })
      .addCase(createReferralCode.failure, (state, action) => {
        state.writes.create = { phase: 'failed', error: action.payload };
      })
      .addCase(updateReferralCode.request, (state) => {
        state.writes.update = { phase: 'loading', error: null };
      })
      .addCase(updateReferralCode.success, (state, action) => {
        state.writes.update = { phase: 'succeeded', error: null };
        const updated = action.payload;
        state.list.items = state.list.items.map((item) =>
          item.id === updated.id ? updated : item,
        );
      })
      .addCase(updateReferralCode.failure, (state, action) => {
        state.writes.update = { phase: 'failed', error: action.payload };
      });
  },
});

export const {
  tabChanged: referralTabChanged,
  writeReset: referralWriteReset,
  noticeDismissed: referralNoticeDismissed,
} = slice.actions;
export const referralCodesReducer = slice.reducer;
